import {
	SendOutlined,
	LikeFilled,
	DislikeFilled,
	MessageFilled,
	CloseOutlined,
	LoadingOutlined,
} from '@ant-design/icons';
import { getInitials } from '../../../../../helpers/getStringInitials';
import { OrderStateType } from '../../../types/OrderStateType';
import './ChatContainer.scss';
import { useState, useMemo, useEffect, useRef, Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import ReactTimeAgo from 'react-time-ago';
import axios from 'axios';
import { API_ENDPOINTS } from '../../../../../assets/api/endpoints';
import { getUserInfo } from '../../../../../helpers/localStorageHandler';
import { MessageType } from '../../../types/MessageType';
import MessageItem from './MessageItem/MessageItem';
import Stomp from 'stompjs';
import TextArea from 'antd/es/input/TextArea';
import { Card, Form, Grid, Skeleton, Tooltip, notification } from 'antd';
import { useLocation } from 'react-router-dom';
import { ActionType } from '../../../types/ActionType';
import { OrderType } from '../../../types/OrderType';
import { getLanguage } from 'helpers/getLanguage';
import { getUserLanguageInCookie } from 'helpers/cookiesHandler';
import EmojiPicker from 'emoji-picker-react';
import { SmileOutlined } from '@ant-design/icons';
import { useDirectionContext } from 'store/DirectionContext';
import { OrderDetailsType } from 'pages/OrderLifeCycle/types/OrderDetailsType';
import { INPUT_CHAT_VALIDATION } from 'assets/config/validation-regex';
import { useConfigurationContext } from 'store/configurationContext';
import FileUploader from './FileUploader';
import ConnectionBanner, { ConnectionBannerStatus } from './ConnectionBanner/ConnectionBanner';
import { useNetworkStatus } from 'hooks/useNetworkStatus';
import { useHoliday } from 'store/HolidayContext';
import wavingHandIcon from '../../../../../assets/images/icons/waving-hand.svg';
import PaymentReleasedChatAlert from './PaymentReleasedChatAlert/PaymentReleasedChatAlert';
import PaymentTransferredChatAlert from './PaymentTransferredChatAlert/PaymentTransferredChatAlert';
import { PaymentReceivedDialog } from '../BodyDetails/ActionsCorner/Dialogs/PaymentReceivedDialog';
import { chatDebug, describeError } from 'helpers/chatDebugLog';
import { PaymentCompletedDialog } from '../BodyDetails/ActionsCorner/Dialogs/PaymentCompletedDialog';

/** A chat message as posted over STOMP or REST. */
type OutgoingChatMessage =
	| { type: 'TEXT'; content: string }
	| { type: 'IMAGE' | 'FILE'; content: ''; fileId: string; fileLabel: string; fileUrl?: string };

type PendingEcho = {
	message: OutgoingChatMessage;
	timer: ReturnType<typeof setTimeout>;
	/** Message ids already known when it was sent, to recognise it in the stored history afterwards. */
	knownIds: Set<number>;
};

// messageId is assigned on save, so an echo is recognised by what we sent: the file id for uploads
// (unique per upload), otherwise the text.
const isSameMessage = (sent: OutgoingChatMessage, received: MessageType) =>
	sent.type === received.type &&
	(sent.type === 'TEXT' ? received.content === sent.content : received.fileId === sent.fileId);

type ChatContainerProps = {
	isMerchant: boolean;
	orderNumber: string;
	orderState: OrderStateType;
	orderType: OrderType;
	actions: ActionType[];
	userName: string;
	orders: number;
	reviews: number;
	rating: number;
	stompClient: Stomp.Client | null;
	/** Tells the socket hook the connection is dead even though stompjs still reports it connected. */
	reportSocketDead: (reason: string) => void;
	/** Websocket JWT of the current connection, sent on STOMP DISCONNECT. */
	socketJwtToken: string | null;
	getOrderDetails: (reason?: string) => void;
	setIsActionsHidden: Dispatch<SetStateAction<boolean>>;
	clientOrder: number;
	clientCompletedOrders: number;
	merchantDetails: OrderDetailsType['merchant'];
	triggerAction: (action: ActionType, reasons?: string, otp?: string) => void;
	otpVlaid: { value: boolean };
	loading: boolean;
	quantity: string;
	amount: string;
};

const iconStyle = {
	borderRadius: '50%',
	width: '30px',
	height: '30px',
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	padding: '5px',
};

const REVIEW_PERCENTAGE = 80;
// While the socket is down, messages are fetched over REST instead so the user can still follow the chat.
const CHAT_POLL_INTERVAL_MS = 4000;
const CHAT_POLL_REASON = 'poll while disconnected';
const CHAT_HISTORY_TIMEOUT_MS = 15000;
// A STOMP send has no acknowledgement, but the server broadcasts every saved message back on the chat
// topic, sender included. That echo is our acknowledgement: no echo within this window = dead socket.
const SEND_ECHO_TIMEOUT_MS = 3000;
// The socket normally connects within a second or two of opening the page; don't flash a warning for that.
const RECONNECTING_BANNER_DELAY_MS = 2000;
const RESTORED_BANNER_MS = 2000;
// Pull-to-refresh on the message list (only while the socket is down; with it up there's nothing to fetch).
const PULL_REFRESH_THRESHOLD_PX = 60;
const PULL_REFRESH_MAX_PX = 90;
// Finger travel is damped so the list follows the pull at half speed, like native pull-to-refresh.
const PULL_REFRESH_RESISTANCE = 0.5;
const UNREADEMESAAGES = 99;
function ChatContainer({
	isMerchant,
	orderNumber,
	orderState,
	orderType,
	actions,
	userName,
	orders,
	reviews,
	rating,
	stompClient,
	reportSocketDead,
	socketJwtToken,
	getOrderDetails,
	setIsActionsHidden,
	clientOrder,
	clientCompletedOrders,
	merchantDetails,
	triggerAction,
	otpVlaid,
	loading,
	quantity,
	amount,
}: ChatContainerProps) {
	const { t } = useTranslation();
	const textField = useRef<null | HTMLElement>(null);
	const location = useLocation();
	const { xs, lg } = Grid.useBreakpoint();
	const { configurationState } = useConfigurationContext();
	const { isTodayHoliday } = useHoliday();
	const [chatForm] = Form.useForm();

	const [showChat, setShowChat] = useState({ value: true });
	const bottomRef = useRef<null | HTMLElement>(null);
	const [userLastSeen, setUserLastSeen] = useState<Date | null>(null);
	const isChatDisabled = useMemo(() => orderState === 'CANCELLED' || orderState === 'REJECTED', [orderState]);
	const [isChatTerminated, setIsChatTernimated] = useState(false);
	const loggedInUserRole = useMemo(() => (isMerchant ? 'MERCHANT' : 'CLIENT'), [isMerchant]);
	const isIncomingMessge = (msg: any) => msg.role !== loggedInUserRole;
	const [isUserOnline, setIsUserOnline] = useState(false);
	const [historicalMessages, setHistoicalMessages] = useState<MessageType[]>([]);
	const [liveMessages, setLiveMessages] = useState<MessageType[]>([]);
	const liveMessageCount = useRef(0);
	liveMessageCount.current = liveMessages.length;
	const hasJoinedChat = useRef(false);
	const historicalMessageCount = useRef(0);
	historicalMessageCount.current = historicalMessages.length;
	// Only the most recently sent history request may update state; on a slow link they can resolve out of order.
	const latestHistoryRequest = useRef(0);
	const historyRequestInFlight = useRef(false);
	const [isChatConnected, setIsChatConnected] = useState(false);
	const [text, setText] = useState('');
	const [unreadMessages, setUnreadMessages] = useState(0);
	const [openEmojiPicker, setOpenEmojiPicker] = useState(false);
	const [disabledSend, setDisabledSend] = useState(false);
	const [sendingMessage, setSendingMessage] = useState(false);
	// Messages sent over STOMP that haven't come back on the chat topic yet.
	const pendingEchoes = useRef<PendingEcho[]>([]);
	const myUserName = useMemo(() => getUserInfo()?.fullName, []);
	const [refreshingChat, setRefreshingChat] = useState(false);
	const [showPaymentReceivedDialog, setShowPaymentReceivedDialog] = useState(false);
	const [showPaymentCompletedDialog, setShowPaymentCompletedDialog] = useState(false);

	const showPaymentReleasedAlert = useMemo(() => {
		if (!xs) return false;
		if (orderState !== 'TRANSFERRED') return false;
		if (orderType === 'BUY' && isMerchant) return true;
		if (orderType === 'SELL' && !isMerchant) return true;
		return false;
	}, [xs, orderState, orderType, isMerchant]);

	const showPaymentTransferredAlert = useMemo(() => xs && actions.includes('CONFIRM_BUYER_TRANSFERRED'), [xs, actions]);

	const handelPaymentReceived = (otp: string) =>
		triggerAction(
			(orderType === 'SELL' && isMerchant) || (orderType === 'BUY' && !isMerchant)
				? 'CONFIRM_BUYER_RECEIVED'
				: 'CONFIRM_SELLER_RECEIVED',
			'',
			otp,
		);
	const handelFinishedSuccessfully = (otp: string) => triggerAction('FINISHED_SUCCESSFULLY', '', otp);
	const handelPaymentCompleted = () => triggerAction('CONFIRM_BUYER_TRANSFERRED');

	// Messages fall back to REST when the socket is down, so typing, sending and uploading only depend on
	// the chat being open.
	const isChatClosed = isChatDisabled || isChatTerminated;
	const chatTopic = useMemo(
		() => API_ENDPOINTS.getOrderChatMsgTopic.replace('%orderNumber%', orderNumber),
		[orderNumber],
	);
	const sendTopic = useMemo(
		() => API_ENDPOINTS.sendOrderChatMsgTopic.replace('%orderNumber%', orderNumber),
		[orderNumber],
	);
	const { directionState } = useDirectionContext();
	// When status is EXPIRE => check for any action in chat
	// YES => show actions + enable chat. NO => hide actions + disable chat
	useEffect(() => {
		if (orderState === 'EXPIRED') {
			const didActionHappen = [...historicalMessages, ...liveMessages].some((msg) => msg.type === 'ACTION');
			setIsActionsHidden(!didActionHappen);
			setIsChatTernimated(!didActionHappen);
		}
	}, [orderState, location.search, historicalMessages, liveMessages]);

	useEffect(() => {
		// Start the WS connect in case it's allowed
		chatDebug.info('chat-ui', 'socket changed', {
			connected: !!stompClient?.connected,
			isChatDisabled,
			isChatTerminated,
			isRejoin: hasJoinedChat.current,
		});
		if (!isChatDisabled && !isChatTerminated && stompClient?.connected) {
			chatDebug.info('chat-ui', 'subscribing and sending JOIN', { orderNumber });
			setIsChatConnected(true);
			stompClient?.subscribe(chatTopic, onMessageReceived);
			stompClient?.send(sendTopic, {}, JSON.stringify({ type: 'JOIN' }));
			// Messages sent while we were disconnected (e.g. a mobile tab suspended in the background) are
			// never replayed over the socket, only stored, so reload them after every reconnect.
			if (hasJoinedChat.current) getChatHistory('chat reconnected');
			hasJoinedChat.current = true;
		}
	}, [stompClient, location.search]);

	useEffect(() => {
		if (isChatConnected && (isChatDisabled || isChatTerminated)) {
			chatDebug.info('chat-ui', 'disconnecting because chat is closed', { isChatDisabled, isChatTerminated });
			stompClient?.disconnect(
				() => {
					setIsChatConnected(false);
				},
				{
					orderNumber,
					...(socketJwtToken ? { Authorization: `Bearer ${socketJwtToken}` } : {}),
				},
			);
		}
	}, [isChatDisabled, isChatTerminated, location.search]);

	const getChatHistory = (reason: string): Promise<MessageType[] | undefined> => {
		// Live messages received before this request are part of the stored history it returns; keep only
		// the ones that arrive while it is in flight and are not in that history yet.
		// Polls don't stack up behind a slow request; event-driven reloads always go out.
		if (reason === CHAT_POLL_REASON && historyRequestInFlight.current) return Promise.resolve(undefined);
		const request = ++latestHistoryRequest.current;
		historyRequestInFlight.current = true;
		const liveCountAtRequest = liveMessageCount.current;
		const startedAt = Date.now();
		chatDebug.info('chat-ui', 'chat history requested', { reason, liveCountAtRequest });
		return axios
			.get(API_ENDPOINTS.getChats.replace('%orderNumber%', orderNumber), {
				timeout: CHAT_HISTORY_TIMEOUT_MS,
			})
			.then(async (response) => {
				const messages: MessageType[] = response.data.chatMessages ?? [];
				// A newer request owns the UI, but the stored messages are still valid for callers.
				if (request !== latestHistoryRequest.current) return messages;
				// Don't yank the user back to the bottom on every poll, only when something new arrived.
				const hasNewMessages = messages.length > historicalMessageCount.current;
				const storedIds = new Set(messages.map((message) => message.messageId));
				chatDebug.info('chat-ui', 'chat history loaded', {
					reason,
					storedMessages: messages.length,
					liveMessagesKept: liveMessageCount.current - liveCountAtRequest,
					tookMs: Date.now() - startedAt,
				});
				setHistoicalMessages(messages);
				setLiveMessages((live) =>
					live.slice(liveCountAtRequest).filter((message) => !message.messageId || !storedIds.has(message.messageId)),
				);
				const doesContainAction = messages?.some((message) => message.type === 'ACTION');
				setIsChatTernimated(!doesContainAction && orderState === 'EXPIRED');
				setUserLastSeen(isMerchant ? response.data.clientLastActive : response.data.merchantLastActive);
				if (reason !== CHAT_POLL_REASON || hasNewMessages) scrollToChatBottom();
				return messages;
			})
			.catch((err: any) => {
				chatDebug.error('chat-ui', 'chat history failed', {
					reason,
					tookMs: Date.now() - startedAt,
					...describeError(err),
				});
				return undefined;
			})
			.finally(() => {
				if (request === latestHistoryRequest.current) historyRequestInFlight.current = false;
			});
	};

	useEffect(() => {
		getChatHistory('page load');
	}, []);

	const isSocketConnected = !!stompClient?.connected;

	// Connection banner, like Messenger's: offline / slow (socket down, REST + polling), then a brief "Connected".
	// Nothing while the socket is up, even on xhr-polling: messages still flow over STOMP there, and
	// the user can't act on a warning anyway.
	const { online } = useNetworkStatus();
	const [reconnectingLongEnough, setReconnectingLongEnough] = useState(false);
	useEffect(() => {
		setReconnectingLongEnough(false);
		if (isSocketConnected) return;
		const timer = setTimeout(() => setReconnectingLongEnough(true), RECONNECTING_BANNER_DELAY_MS);
		return () => clearTimeout(timer);
	}, [isSocketConnected]);

	let connectionProblem: ConnectionBannerStatus | null = null;
	if (!online) connectionProblem = 'offline';
	else if (!isSocketConnected) connectionProblem = reconnectingLongEnough ? 'reconnecting' : null;

	const [showRestored, setShowRestored] = useState(false);
	const hadConnectionProblem = useRef(false);
	useEffect(() => {
		if (connectionProblem) {
			hadConnectionProblem.current = true;
			setShowRestored(false);
			return;
		}
		if (!hadConnectionProblem.current) return;
		hadConnectionProblem.current = false;
		setShowRestored(true);
		const timer = setTimeout(() => setShowRestored(false), RESTORED_BANNER_MS);
		return () => clearTimeout(timer);
	}, [connectionProblem]);

	const connectionBannerStatus: ConnectionBannerStatus | null = isChatClosed
		? null
		: connectionProblem ?? (showRestored ? 'restored' : null);
	useEffect(() => {
		if (isSocketConnected || isChatDisabled || isChatTerminated) return;
		chatDebug.info('chat-ui', 'socket down, polling chat history', { intervalMs: CHAT_POLL_INTERVAL_MS });
		const interval = setInterval(() => {
			if (document.visibilityState === 'visible') getChatHistory(CHAT_POLL_REASON);
		}, CHAT_POLL_INTERVAL_MS);
		return () => clearInterval(interval);
	}, [isSocketConnected, isChatDisabled, isChatTerminated, orderNumber, orderState]);

	const refreshingChatRef = useRef(false);
	refreshingChatRef.current = refreshingChat;
	const [pullDistance, setPullDistance] = useState(0);
	const pullToRefreshEnabled = !isChatClosed && !isSocketConnected;

	const refreshChat = (reason: string) => {
		if (refreshingChatRef.current) return;
		setRefreshingChat(true);
		getOrderDetails(reason);
		// Keep the spinner up briefly so a fast response still reads as a refresh.
		Promise.all([getChatHistory(reason), new Promise((resolve) => setTimeout(resolve, 500))]).finally(() =>
			setRefreshingChat(false),
		);
	};
	const refreshChatRef = useRef(refreshChat);
	refreshChatRef.current = refreshChat;

	useEffect(() => {
		const list = bottomRef.current;
		setPullDistance(0);
		if (!list || !pullToRefreshEnabled) return;
		let startY: number | null = null;
		let distance = 0;

		const onTouchStart = (event: TouchEvent) => {
			// Only a pull that starts with the list at its very top, like native pull-to-refresh.
			startY = list.scrollTop <= 0 && !refreshingChatRef.current ? event.touches[0].clientY : null;
			distance = 0;
		};
		const onTouchMove = (event: TouchEvent) => {
			if (startY === null) return;
			const dragged = event.touches[0].clientY - startY;
			if (dragged <= 0 || list.scrollTop > 0) {
				distance = 0;
				setPullDistance(0);
				return;
			}
			// Keep the page (and the browser's own pull-to-reload) from moving while we handle the pull.
			event.preventDefault();
			distance = Math.min(dragged * PULL_REFRESH_RESISTANCE, PULL_REFRESH_MAX_PX);
			setPullDistance(distance);
		};
		const onTouchEnd = () => {
			if (startY === null) return;
			startY = null;
			if (distance >= PULL_REFRESH_THRESHOLD_PX) refreshChatRef.current('pull to refresh');
			distance = 0;
			setPullDistance(0);
		};

		list.addEventListener('touchstart', onTouchStart, { passive: true });
		// Not passive: preventDefault is what stops the page from scrolling along.
		list.addEventListener('touchmove', onTouchMove, { passive: false });
		list.addEventListener('touchend', onTouchEnd);
		list.addEventListener('touchcancel', onTouchEnd);
		return () => {
			list.removeEventListener('touchstart', onTouchStart);
			list.removeEventListener('touchmove', onTouchMove);
			list.removeEventListener('touchend', onTouchEnd);
			list.removeEventListener('touchcancel', onTouchEnd);
		};
		// showChat: closing and reopening the chat box mounts a new list element.
	}, [pullToRefreshEnabled, showChat]);

	const pullHintKey = refreshingChat
		? 'refreshingMessages'
		: pullDistance >= PULL_REFRESH_THRESHOLD_PX
		? 'releaseToRefresh'
		: 'pullToRefresh';

	// After a suspend the socket can still look connected until the heartbeat notices it is dead, so
	// don't wait for the reconnect to fill in what was missed.
	useEffect(() => {
		const handleVisibility = () => {
			if (document.visibilityState === 'visible' && !isChatDisabled && !isChatTerminated)
				getChatHistory('page visible');
		};
		document.addEventListener('visibilitychange', handleVisibility);
		return () => document.removeEventListener('visibilitychange', handleVisibility);
	}, [isChatDisabled, isChatTerminated, orderNumber]);

	useEffect(
		() => () => {
			pendingEchoes.current.forEach((pending) => clearTimeout(pending.timer));
			pendingEchoes.current = [];
		},
		[orderNumber],
	);

	const onMessageReceived = (data: any) => {
		const newMessage: MessageType = JSON.parse(data.body);
		if (!isIncomingMessge(newMessage)) {
			const echoed = pendingEchoes.current.find((pending) => isSameMessage(pending.message, newMessage));
			if (echoed) {
				clearTimeout(echoed.timer);
				pendingEchoes.current = pendingEchoes.current.filter((pending) => pending !== echoed);
			}
		}
		// Type and direction only; message contents stay out of the logs.
		chatDebug.info('chat-ui', 'message received', {
			type: newMessage.type,
			incoming: isIncomingMessge(newMessage),
			hasMessageId: !!newMessage.messageId,
		});
		setLiveMessages((oldMessages) => [...oldMessages, newMessage]);
		if (isIncomingMessge(newMessage)) {
			if (newMessage.messageId) {
				setHistoicalMessages((messages) =>
					messages.map((message) => ({ ...message, read: true, delivered: newMessage.delivered })),
				);
				setLiveMessages((messages) =>
					messages.map((message) => ({ ...message, read: true, delivered: newMessage.delivered })),
				);
			}
			if (newMessage.type === 'DELIVERED') {
				setHistoicalMessages((messages) => messages.map((message) => ({ ...message, delivered: true })));
				setLiveMessages((messages) => messages.map((message) => ({ ...message, delivered: true })));
			}
			if (
				newMessage.type === 'TEXT' ||
				newMessage.type === 'FILE' ||
				newMessage.type === 'ACTION' ||
				newMessage.type === 'JOIN'
			) {
				setShowChat((prevState) => {
					if (prevState.value) {
						sendReceivedNotification(newMessage);
					} else {
						setUnreadMessages((count) => count + 1);
					}
					return prevState;
				});
			}

			if (newMessage.type === 'RECEIVED' || newMessage.type === 'JOIN') {
				setIsUserOnline(true);
			} else if (newMessage.type === 'LEAVE') {
				setIsUserOnline(false);
				setUserLastSeen(new Date());
			}
		}

		if (newMessage.type === 'TERMINATED') {
			setIsChatTernimated(true);
		} else if (newMessage.type === 'ACTION') {
			getOrderDetails('ACTION message received');
		}

		scrollToChatBottom();
	};

	const onChatKeyUp = (event: any) => {
		event.preventDefault();

		if (event.keyCode === 13 && !disabledSend && !sendingMessage) {
			onMessageSend();
		}
	};

	const clearMessageInput = () => {
		chatForm.setFieldValue('currentMessage', '');
		setText('');
	};

	// REST send: used when the socket is down, and to recover a STOMP send that never echoed back.
	const sendOverRest = (message: OutgoingChatMessage, reason: string) => {
		const startedAt = Date.now();
		return axios.post(API_ENDPOINTS.sendOrderChatMessage.replace('%orderNumber%', orderNumber), message).then(() => {
			chatDebug.info('chat-ui', 'message sent over REST', {
				type: message.type,
				reason,
				tookMs: Date.now() - startedAt,
			});
			// The broadcast may not reach us without a working socket, so load it from history.
			getChatHistory(`message sent over REST (${reason})`);
		});
	};

	const onUnechoedMessage = async (pending: PendingEcho) => {
		pendingEchoes.current = pendingEchoes.current.filter((other) => other !== pending);
		chatDebug.warn('chat-ui', 'sent message was not echoed back, socket considered dead', {
			timeoutMs: SEND_ECHO_TIMEOUT_MS,
		});
		reportSocketDead(`sent message not echoed within ${SEND_ECHO_TIMEOUT_MS / 1000}s`);
		// On a slow link the STOMP frame may have been saved and only the echo is late; resending blindly
		// would post it twice. The stored history decides.
		const stored = await getChatHistory('sent message not echoed');
		const alreadySaved = stored?.some(
			(message) =>
				!isIncomingMessge(message) &&
				isSameMessage(pending.message, message) &&
				!pending.knownIds.has(message.messageId),
		);
		if (alreadySaved) {
			chatDebug.info('chat-ui', 'unechoed message was saved by the server, not resending');
			return;
		}
		sendOverRest(pending.message, 'no echo').catch((err: any) => {
			chatDebug.error('chat-ui', 'resending unechoed message failed', {
				type: pending.message.type,
				...describeError(err),
			});
			onMessageNotSent(pending.message);
		});
	};

	const onMessageNotSent = (message: OutgoingChatMessage) => {
		if (message.type === 'TEXT') {
			// Give the text back so it isn't lost; don't overwrite something the user has typed since.
			if (!chatForm.getFieldValue('currentMessage')) {
				chatForm.setFieldValue('currentMessage', message.content);
				setText(message.content);
			}
			return;
		}
		// The file is uploaded but its chat message didn't go out; the user has to share it again.
		notification.error({ message: '', description: t<string>('somethingWentWrong'), placement: 'bottom' });
	};

	/**
	 * Posts a chat message: over STOMP with the echo as acknowledgement while the socket is up, over REST
	 * when it's down. Resolves once it's sent (or handed to the echo check), rejects if REST fails.
	 */
	const postChatMessage = (message: OutgoingChatMessage): Promise<void> => {
		if (stompClient?.connected) {
			stompClient.send(sendTopic, {}, JSON.stringify({ sender: myUserName, ...message }));
			const knownIds = new Set([...historicalMessages, ...liveMessages].map((stored) => stored.messageId));
			const pending: PendingEcho = {
				message,
				knownIds,
				timer: setTimeout(() => onUnechoedMessage(pending), SEND_ECHO_TIMEOUT_MS),
			};
			pendingEchoes.current.push(pending);
			chatDebug.info('chat-ui', 'message sent over STOMP, waiting for echo', {
				type: message.type,
				timeoutMs: SEND_ECHO_TIMEOUT_MS,
			});
			return Promise.resolve();
		}
		return sendOverRest(message, 'socket down');
	};

	const onMessageSend = () => {
		const content = chatForm.getFieldValue('currentMessage');
		if (sendingMessage || !content?.trim()) return;

		if (stompClient?.connected) {
			postChatMessage({ type: 'TEXT', content });
			clearMessageInput();
			return;
		}

		setSendingMessage(true);
		postChatMessage({ type: 'TEXT', content })
			.then(clearMessageInput)
			.catch((err: any) => {
				// The text stays in the input so the user can retry.
				chatDebug.error('chat-ui', 'message send failed', describeError(err));
			})
			.finally(() => setSendingMessage(false));
	};

	const sendReceivedNotification = (message: any) => {
		stompClient?.send(
			sendTopic,
			{},
			JSON.stringify({ messageId: message.messageId, role: loggedInUserRole, type: 'RECEIVED' }),
		);
	};

	const scrollToChatBottom = () => {
		setTimeout(() => {
			bottomRef?.current?.scroll({
				top: Number(document.querySelector('.chat-message-container')?.clientHeight || 0),
				left: 0,
				behavior: 'smooth',
			});
		}, 500);
	};

	const handleUploadFileClick = (event: any, type: 'IMAGE' | 'FILE') => {
		const files = event.target?.files;
		const uploadedFile = files[0];

		if (!uploadedFile) {
			return;
		}

		const url = type === 'IMAGE' ? API_ENDPOINTS.uploadImage + orderNumber : API_ENDPOINTS.uploadFile + orderNumber;
		const formData = new FormData();

		formData.append('file', uploadedFile);

		axios
			.post(url, formData, {
				headers: { 'Content-Type': 'multipart/form-data' },
			})
			.then((result) => {
				if (!result) return;
				// Same path as text: STOMP + echo check when connected, REST when not, so the file message
				// isn't lost if the socket is dead.
				const fileMessage: OutgoingChatMessage = {
					type,
					content: '',
					fileId: result.data.fileId,
					fileLabel: result.data.fileLabel,
					fileUrl: result.data.fileUrl ?? undefined,
				};
				return postChatMessage(fileMessage).catch((err: any) => {
					chatDebug.error('chat-ui', 'file message send failed', { type, ...describeError(err) });
					onMessageNotSent(fileMessage);
				});
			})
			.catch((error) => {
				chatDebug.error('chat-ui', 'file upload failed', { type, ...describeError(error) });
			});
	};

	useEffect(() => {
		setHistoicalMessages([]);
		setLiveMessages([]);
	}, [location.search]);
	useEffect(() => {
		if (showChat.value && stompClient?.connected) {
			const historicalMessagesReversed = [...historicalMessages].reverse();
			if (historicalMessagesReversed[0]) {
				sendReceivedNotification(historicalMessagesReversed[0]);
			}
		} else {
			setUnreadMessages(0);
		}
	}, [showChat, stompClient?.connected]);

	const handleEmojiClick = (emojiData: any) => {
		setOpenEmojiPicker(false);
		const content = chatForm.getFieldValue('currentMessage') ?? '';
		setText((prevMessage) => prevMessage + emojiData.emoji);
		chatForm.setFieldValue('currentMessage', content + emojiData.emoji);
		textField.current?.focus();
	};
	useEffect(() => setShowChat({ value: !Boolean(xs) }), [xs]);
	const themeColor = configurationState.find((item) => item.configKey === 'theme_color')?.value || '';
	const secondary_color = configurationState.find((item) => item.configKey === 'secondary_color')?.value || '';

	return (
		<div className='chatting-with-other-party-container flex justify-end'>
			{orderState ? (
				<>
					{showChat.value ? null : (
						<div className='open-chat-trigger'>
							{xs ? (
								<div
									className='mobile-chat-button-container'
									onClick={() => setShowChat({ value: true })}
									style={{
										marginLeft: directionState === 'rtl' ? '35px' : '',
									}}
								>
									<button
										className='open-chat-button mobile-chat-button rounded-xl'
										style={{ backgroundColor: themeColor }}
									>
										{unreadMessages ? (
											<span
												className='bill-icon'
												style={{
													border: `1px solid ${secondary_color}`,
													backgroundColor: themeColor,
												}}
											>
												{unreadMessages > UNREADEMESAAGES ? `+${UNREADEMESAAGES}` : unreadMessages}
											</span>
										) : (
											<span className='bill-icon-empty'></span>
										)}
										<MessageFilled style={{ color: secondary_color }} />
									</button>
									<span className='open-chat-button-label'>
										{isMerchant ? t<string>('chatWithClient') : t<string>('chatWithMerchant')}
									</span>
									<img src={wavingHandIcon} alt='' className='h-5 w-5' aria-hidden />
								</div>
							) : (
								<button
									className='open-chat-button  rounded-xl'
									style={{ backgroundColor: themeColor }}
									onClick={() => setShowChat({ value: true })}
								>
									{unreadMessages ? (
										<span
											className='bill-icon'
											style={{
												border: `1px solid ${secondary_color}`,
												backgroundColor: themeColor,
											}}
										>
											{unreadMessages > UNREADEMESAAGES ? `+${UNREADEMESAAGES}` : unreadMessages}
										</span>
									) : (
										<span className='bill-icon-empty'></span>
									)}
									<MessageFilled style={{ color: secondary_color }} />
								</button>
							)}
						</div>
					)}
					<div className={`order-life-cycle-chat-container ${showChat.value ? '' : 'hidden'}`}>
						<div
							className='order-life-cycle-chat-box-header'
							style={{ backgroundColor: themeColor, color: secondary_color }}
						>
							<div className='header-container'>
								<span className='user-menu-details-initials'>{getInitials(userName)}</span>
								<div className='user-details flex justify-between'>
									<div className='flex flex-col justify-center w-[60%]'>
										<p className='user-name'>
											{userName}
											{!isMerchant && (
												<span
													className={`text-xs mx-1 font-normal ${
														rating < REVIEW_PERCENTAGE ? 'text-[#F6465D]' : 'text-[#33A867]'
													}`}
												>
													{rating < REVIEW_PERCENTAGE ? (
														<DislikeFilled className='rating-icon text-[#F6465D]' />
													) : (
														<LikeFilled className='rating-icon p-0 m-0 text-[#33A867]' />
													)}
													{rating}%
												</span>
											)}
										</p>
										<div className='content text-[#acb8b1]'>
											{isMerchant ? (
												<>
													<span dir='ltr' className='text-[10px]'>
														{Number(clientCompletedOrders)} {t<string>('orders')}
													</span>
													<span className='vertical-seperator'></span>
													<span dir='ltr' className='text-[10px]'>
														{(
															(Number(clientCompletedOrders) /
																(clientOrder === null || clientOrder === 0 ? 1 : Number(clientOrder))) *
															100
														).toFixed()}
														% {t<string>('completionRate')}
													</span>
												</>
											) : (
												<>
													<span dir='ltr' className='text-[10px]'>
														{merchantDetails?.ordersCompleted} {t<string>('orders')}
													</span>
													<span className='vertical-seperator'></span>
													<span dir='ltr' className='text-[10px]'>
														{(
															(Number(merchantDetails?.ordersCompleted ?? 0) /
																(merchantDetails?.totalOrders === null || merchantDetails?.totalOrders === 0
																	? 1
																	: Number(merchantDetails?.totalOrders))) *
															100
														).toFixed()}
														% {t<string>('completionRate')}
													</span>
												</>
											)}
										</div>
									</div>
									<div className='flex flex-col items-end justify-between'>
										<CloseOutlined
											className='hover:shadow-2xl shadow-cyan-300 justify-end text-[12px] mt-1'
											alt='close'
											onClick={() => setShowChat({ value: false })}
										/>
										{isUserOnline ? (
											<p className='live text-[10px] text-[#acb8b1]' style={{ paddingBottom: '3px' }}>
												{t<string>('live')}
											</p>
										) : userLastSeen ? (
											<p className='last-seen text-[10px] text-[#acb8b1]' id='chat_last-seen'>
												<ReactTimeAgo
													className='text-[#acb8b1]'
													date={new Date(userLastSeen).getTime()}
													locale={getLanguage(getUserLanguageInCookie())}
												/>
											</p>
										) : null}
									</div>
								</div>
							</div>
						</div>
						<div className='order-life-cycle-chat-box-body'>
							{connectionBannerStatus ? <ConnectionBanner status={connectionBannerStatus} /> : null}
							{showPaymentReleasedAlert ? (
								<PaymentReleasedChatAlert
									onFundsReceivedClick={() => setShowPaymentReceivedDialog(true)}
									disabled={isChatDisabled || isChatTerminated}
								/>
							) : null}
							{showPaymentTransferredAlert ? (
								<PaymentTransferredChatAlert
									onFundsTransferredClick={() => setShowPaymentCompletedDialog(true)}
									disabled={isChatDisabled || isChatTerminated}
									isMerchant={isMerchant}
								/>
							) : null}
							{pullToRefreshEnabled && (pullDistance > 0 || refreshingChat) ? (
								<div
									className='chat-pull-to-refresh'
									id='chat_pull-to-refresh'
									role='status'
									aria-live='polite'
									style={{ height: refreshingChat ? 36 : pullDistance }}
								>
									{refreshingChat ? <LoadingOutlined /> : null}
									<span>{t<string>(pullHintKey)}</span>
								</div>
							) : null}
							<div className='chat-messages-scroll' ref={bottomRef as any}>
								<div className='chat-message-container' id='chat_message-container'>
									<Card
										className='chat-card'
										style={{
											width: 300,
											backgroundColor: themeColor,
											color: secondary_color,
											padding: '5px !important',
											opacity: '.8',
											marginBottom: '6px',
										}}
									>
										<p className='text-center text-[10px]'>
											<span className='text-[16px]'>👋</span>
											{t<string>('welcomeChatMessage')}
										</p>
									</Card>
									{[...historicalMessages, ...liveMessages].map((message, index) => (
										<MessageItem
											isMerchant={isMerchant}
											message={message}
											orderNumber={orderNumber}
											key={index}
											userName={userName}
										/>
									))}
								</div>
							</div>
						</div>
						<div className='order-life-cycle-chat-box-footer'>
							{isChatDisabled ? (
								<span style={{ width: '100%', textAlign: 'center', fontSize: '14px', color: '#bdbdbd' }}>
									{t<string>('chatIsDisabled')}
								</span>
							) : (
								<Form
									name='chatForm'
									form={chatForm}
									className='chat-form'
									onValuesChange={() => setText(chatForm.getFieldValue('currentMessage')?.trim())}
								>
									<Form.Item style={{ width: '26px', display: 'flex', alignItems: 'center' }}>
										<FileUploader
											disabled={isTodayHoliday || isChatClosed}
											style={{ ...iconStyle, color: secondary_color, backgroundColor: themeColor }}
											handleUploadFileClick={handleUploadFileClick}
											isTodayHoliday={isTodayHoliday}
										/>
									</Form.Item>
									<Form.Item
										name='currentMessage'
										rules={[
											{
												validator: (_, value) => {
													if (INPUT_CHAT_VALIDATION.test(value)) {
														setDisabledSend(false);
														return Promise.resolve();
													}
													setDisabledSend(true);
													return Promise.reject(new Error(t('validationChatMessage') as string));
												},
											},
										]}
									>
										<TextArea
											id='chat_message'
											ref={textField}
											disabled={isChatClosed}
											placeholder={t<string>('typeMessageHere')}
											autoSize={{ minRows: 1, maxRows: 3 }}
											onPressEnter={onChatKeyUp}
											style={{ paddingInlineEnd: '30px' }}
										/>
									</Form.Item>
									{lg && (
										<SmileOutlined
											className='custom-icon'
											onClick={() => setOpenEmojiPicker((prvState) => !prvState)}
											style={{
												left: directionState === 'rtl' ? '60px' : 'unset',
												right: directionState === 'ltr' ? '60px' : 'unset',
											}}
										/>
									)}
									<EmojiPicker open={openEmojiPicker} onEmojiClick={handleEmojiClick} className='emoji-picker' />
									<Tooltip title={isTodayHoliday ? t<string>('holidayDay') : ''}>
										<button
											id='chat_send-message-button'
											disabled={isTodayHoliday || isChatClosed || !text || disabledSend || sendingMessage}
											onClick={onMessageSend}
										>
											<SendOutlined
												style={{
													...iconStyle,
													backgroundColor: themeColor,
													color: secondary_color,
													transform: 'rotate(-30deg)',
													padding: '5px 5px 5px 7px',
												}}
											/>
										</button>
									</Tooltip>
								</Form>
							)}
						</div>
					</div>
				</>
			) : (
				<Skeleton active paragraph={{ rows: 7 }} />
			)}
			<PaymentReceivedDialog
				showPaymentReceivedDialog={showPaymentReceivedDialog}
				setShowPaymentReceivedDialog={setShowPaymentReceivedDialog}
				handelPaymentReceived={handelPaymentReceived}
				handelFinishedSuccessfully={handelFinishedSuccessfully}
				isFinished={actions.includes('FINISHED_SUCCESSFULLY')}
				userName={userName}
				quantity={quantity}
				amount={amount}
				orderNumber={orderNumber}
				otpVlaid={otpVlaid}
				loading={loading}
			/>
			<PaymentCompletedDialog
				showPaymentCompletedDialog={showPaymentCompletedDialog}
				setShowPaymentCompletedDialog={setShowPaymentCompletedDialog}
				handelPaymentCompleted={handelPaymentCompleted}
				orderNumber={orderNumber}
				getOrderDetails={getOrderDetails}
				loading={loading}
			/>
		</div>
	);
}

export default ChatContainer;
